import {css, keyframes, styled} from '@mui/material/styles';
import PropTypes from 'prop-types';
import React from 'react';

const getSm = (p) =>
  p.theme?.breakpoints?.sm ? p.theme.breakpoints.sm : '600px';

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const shimmerBg = (from, mid, to) => css`
  background: linear-gradient(90deg, ${from} 25%, ${mid} 50%, ${to} 75%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s infinite;
`;

const Banner = styled('div')`
  background: #f1f4fe;
  border-radius: 0.5rem;
  height: 30.5625rem;
  margin: 2.5rem 0;
  overflow: hidden;
  position: relative;
  display: flex;

  @media (max-width: ${getSm}) {
    flex-direction: column;
    height: auto;
  }
`;

const BannerImage = styled('div')`
  ${shimmerBg('#e8ebf9', '#f1f4fe', '#e8ebf9')};
  flex-shrink: 0;
  height: 100%;
  width: 33.3125rem;

  @media (max-width: ${getSm}) {
    height: 18.875rem;
    width: 100%;
  }
`;

const BannerContent = styled('div')`
  flex: 1;
  padding: 1.875rem 1.5rem;
  display: flex;
  flex-direction: column;

  @media (max-width: ${getSm}) {
    padding: 1.5rem 1rem;
  }
`;

const BannerHeader = styled('div')`
  height: 2rem;
  width: 12.5rem;
  ${shimmerBg('#dce0f2', '#e8ebf9', '#dce0f2')};
  border-radius: 0.25rem;
  margin: 1.5rem 0;
`;

const BannerCards = styled('div')`
  display: flex;
  gap: 1.5rem;
  flex: 1;
  overflow: hidden;

  @media (max-width: ${getSm}) {
    gap: 0.5rem;
  }
`;

const BannerCard = styled('div')`
  flex: 0 0 calc(100% / 3.5 - 1rem);
  ${shimmerBg('#dce0f2', '#e8ebf9', '#dce0f2')};
  border-radius: 0.5rem;
  min-height: 100%;

  @media (max-width: ${getSm}) {
    flex: 0 0 calc(100% / 1.6 - 0.25rem);
    min-height: 18.9375rem;
  }
`;

const CarouselWrap = styled('div')`
  margin: 2.5rem 0;
`;

const CarouselHeader = styled('div')`
  height: 2rem;
  width: 12.5rem;
  ${shimmerBg('#e5e5e5', '#f0f0f0', '#e5e5e5')};
  border-radius: 0.25rem;
  margin-bottom: 1.5rem;
`;

const CarouselCards = styled('div')`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;

  @media (max-width: ${getSm}) {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.5rem;
  }
`;

const CarouselCard = styled('div')`
  ${shimmerBg('#f5f5f5', '#fafafa', '#f5f5f5')};
  border-radius: 0.5rem;
  padding-top: 100%;
  position: relative;
`;

const CarouselCardContent = styled('div')`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 1rem;
  background: white;
  border-radius: 0 0 0.5rem 0.5rem;
`;

const CarouselCardTitle = styled('div')`
  height: 1rem;
  ${shimmerBg('#e5e5e5', '#f0f0f0', '#e5e5e5')};
  border-radius: 0.125rem;
  margin-bottom: 0.5rem;
`;

const CarouselCardPrice = styled('div')`
  height: 0.875rem;
  width: 60%;
  ${shimmerBg('#e5e5e5', '#f0f0f0', '#e5e5e5')};
  border-radius: 0.125rem;
`;

export const BannerSkeleton = () => (
  <Banner data-testid="banner-skeleton">
    <BannerImage />
    <BannerContent>
      <BannerHeader />
      <BannerCards>
        <BannerCard />
        <BannerCard />
        <BannerCard />
        <BannerCard />
      </BannerCards>
    </BannerContent>
  </Banner>
);

export const CarouselSkeleton = () => (
  <CarouselWrap data-testid="carousel-skeleton">
    <CarouselHeader />
    <CarouselCards>
      <CarouselCard>
        <CarouselCardContent>
          <CarouselCardTitle />
          <CarouselCardPrice />
        </CarouselCardContent>
      </CarouselCard>
      <CarouselCard>
        <CarouselCardContent>
          <CarouselCardTitle />
          <CarouselCardPrice />
        </CarouselCardContent>
      </CarouselCard>
      <CarouselCard>
        <CarouselCardContent>
          <CarouselCardTitle />
          <CarouselCardPrice />
        </CarouselCardContent>
      </CarouselCard>
      <CarouselCard>
        <CarouselCardContent>
          <CarouselCardTitle />
          <CarouselCardPrice />
        </CarouselCardContent>
      </CarouselCard>
    </CarouselCards>
  </CarouselWrap>
);

const LoadingSkeleton = ({variant}) => {
  if (variant === 'banner') {
    return <BannerSkeleton />;
  }

  if (variant === 'carousel') {
    return <CarouselSkeleton />;
  }

  return null;
};

LoadingSkeleton.propTypes = {
  variant: PropTypes.oneOf(['banner', 'carousel']).isRequired
};

export default LoadingSkeleton;
